import { create } from 'zustand'
import type { AgentStatus, BuyerJourneyStage, ContentType, NodeName, NodeStatus, StreamPublishQueueItem } from '@/lib/types'

export interface ChannelPlanEntry {
  rationale: string
  posts_per_week: number
}

export interface ContentSlot {
  slot_id: string
  channel: string
  content: string | null
}

export interface TokenUsageCounts {
  input_tokens: number
  output_tokens: number
  total_tokens: number
}

interface GenerateState {
  streamBuffer: string
  runId: string | null
  clientId: string | null
  selectedTypes: ContentType[]
  threadId: string | null
  checkpointId: string | null
  agentStatus: AgentStatus
  buyerJourneyStage: BuyerJourneyStage | null
  nodeStatuses: Partial<Record<NodeName, NodeStatus>>
  errorMessage: string | null
  channelPlan: Record<string, ChannelPlanEntry> | null
  contentSlots: ContentSlot[] | null
  publishQueue: StreamPublishQueueItem[] | null
  liveTokenUsage: TokenUsageCounts
  activeModel: string | null
  setRunId: (id: string) => void
  setClientId: (id: string) => void
  setSelectedTypes: (types: ContentType[]) => void
  setThreadId: (id: string) => void
  setCheckpointId: (id: string) => void
  setAgentStatus: (s: AgentStatus) => void
  setBuyerJourneyStage: (stage: BuyerJourneyStage) => void
  setErrorMessage: (msg: string | null) => void
  setChannelPlan: (plan: Record<string, ChannelPlanEntry>) => void
  setContentSlots: (slots: ContentSlot[]) => void
  setPublishQueue: (items: StreamPublishQueueItem[]) => void
  addTokenUsage: (usage: Partial<TokenUsageCounts>) => void
  appendBuffer: (chunk: string) => void
  setNodeStatus: (node: NodeName, status: NodeStatus) => void
  setActiveModel: (model: string) => void
  reset: () => void
}

const ZERO_USAGE: TokenUsageCounts = { input_tokens: 0, output_tokens: 0, total_tokens: 0 }

const initialState = {
  streamBuffer: '',
  runId: null,
  clientId: null,
  selectedTypes: [] as ContentType[],
  threadId: null,
  checkpointId: null,
  agentStatus: 'idle' as AgentStatus,
  buyerJourneyStage: null as BuyerJourneyStage | null,
  nodeStatuses: {} as Partial<Record<NodeName, NodeStatus>>,
  errorMessage: null,
  channelPlan: null,
  contentSlots: null,
  publishQueue: null,
  liveTokenUsage: ZERO_USAGE,
  activeModel: null as string | null,
}

export const useGenerateStore = create<GenerateState>((set) => ({
  ...initialState,
  setRunId: (runId) => set({ runId }),
  setClientId: (clientId) => set({ clientId }),
  setSelectedTypes: (selectedTypes) => set({ selectedTypes }),
  setThreadId: (threadId) => set({ threadId }),
  setCheckpointId: (checkpointId) => set({ checkpointId }),
  setAgentStatus: (agentStatus) => set({ agentStatus }),
  setBuyerJourneyStage: (buyerJourneyStage) => set({ buyerJourneyStage }),
  setErrorMessage: (errorMessage) => set({ errorMessage }),
  setChannelPlan: (channelPlan) => set({ channelPlan }),
  setContentSlots: (contentSlots) => set({ contentSlots }),
  setPublishQueue: (publishQueue) => set({ publishQueue }),
  addTokenUsage: (usage) =>
    set((s) => ({
      liveTokenUsage: {
        input_tokens:  s.liveTokenUsage.input_tokens  + (usage.input_tokens  ?? 0),
        output_tokens: s.liveTokenUsage.output_tokens + (usage.output_tokens ?? 0),
        total_tokens:  s.liveTokenUsage.total_tokens  + (usage.total_tokens  ?? 0),
      },
    })),
  appendBuffer: (chunk) => set((s) => ({ streamBuffer: s.streamBuffer + chunk })),
  setNodeStatus: (node, status) =>
    set((s) => ({ nodeStatuses: { ...s.nodeStatuses, [node]: status } })),
  setActiveModel: (activeModel) => set({ activeModel }),
  reset: () => set(initialState),
}))
